import React, { useContext, useState } from 'react';
import ClientProvider from '../../../../Various/ClientProvider';
import CampaignsOverview from '../shared/CampaignsOverview';
import LoadCampaignDataButton from '../shared/LoadCampaignDataButton';
import ActionButtonGroup, { ActionButton } from '../shared/ActionButtonGroup';
import { iterateCampaigns, initializeTableValues } from '../../utils/campaignHelpers';
import { useBatchJobSender } from '../../hooks/useBatchJobSender';

const SendReportsTab = ({ 
    clientCampaigns, 
    convertTimestampsToDates, 
    onTableRowClick,
    tableValues,
    setTableValues,
    invoicingFilter,
    setInvoicingFilter,
    triggerTableRerender,
    tableRerenderKey = 0,
    loadAllCampaignsWithHistoryAndOpenBillingData
}) => {
    const wsProvider = useContext(ClientProvider);
    const [updateReportHistory, setUpdateReportHistory] = useState(true);

    const setDefaultDates = () => {
        let hasChanges = false;

        setTableValues(prev => {
            const newTableValues = initializeTableValues(prev, 'sendReports');

            iterateCampaigns(clientCampaigns, convertTimestampsToDates, (campaign, campaignId) => {
                // Only process campaigns with open report data
                if (campaign.open_report_data) {
                    // Set default dates from backend data
                    if (!newTableValues.sendReportsFromDate[campaignId]) {
                        newTableValues.sendReportsFromDate[campaignId] = campaign.open_report_data.start_date;
                        hasChanges = true;
                    }
                    if (!newTableValues.sendReportsToDate[campaignId]) {
                        newTableValues.sendReportsToDate[campaignId] = campaign.open_report_data.end_date;
                        hasChanges = true;
                    }
                }
            });

            if (hasChanges) {
                console.log("newTableValues", newTableValues);
            }
            
            return hasChanges ? newTableValues : prev;
        });
        
        return hasChanges;
    };

    const applyAutoSelection = () => {
        let hasChanges = false;

        setTableValues(prev => {
            const newTableValues = initializeTableValues(prev, 'sendReports');

            iterateCampaigns(clientCampaigns, convertTimestampsToDates, (campaign, campaignId) => {
                // Only process campaigns with open report data
                if (campaign.open_report_data) {
                    // Set action based on backend's report_ready decision
                    const currentAction = newTableValues.sendReportsAction[campaignId];
                    if (!currentAction || currentAction === 'no_action') {
                        newTableValues.sendReportsAction[campaignId] = campaign.open_report_data.report_ready ? 'send_report' : 'no_action';
                        hasChanges = true;
                    }
                }
            });

            return hasChanges ? newTableValues : prev;
        });
        
        return hasChanges;
    };

    const applySelectAll = () => {
        let hasChanges = false;

        setTableValues(prev => {
            const newTableValues = initializeTableValues(prev, 'sendReports');

            iterateCampaigns(clientCampaigns, convertTimestampsToDates, (campaign, campaignId) => {
                // Only process campaigns with open report data
                if (campaign.open_report_data) {
                    // Select all campaigns with open report data
                    const currentAction = newTableValues.sendReportsAction[campaignId];
                    if (!currentAction || currentAction === 'no_action') {
                        newTableValues.sendReportsAction[campaignId] = 'send_report';
                        hasChanges = true;
                    }
                }
            });

            return hasChanges ? newTableValues : prev;
        });
        
        return hasChanges;
    };

    const handleAutoSelect = () => {
        // First set the dates, then apply auto-selection
        const datesChanged = setDefaultDates();
        const selectionChanged = applyAutoSelection();
        
        if (!datesChanged && !selectionChanged) {
            alert('No changes were made. All campaigns may already be configured or no campaigns meet the auto-selection criteria.');
        } else {
            triggerTableRerender();
        }
    };

    const handleSelectAll = () => {
        // First set the dates, then select all
        const datesChanged = setDefaultDates();
        const selectionChanged = applySelectAll();
        
        if (!datesChanged && !selectionChanged) {
            alert('No campaigns with open report data found.');
        } else {
            triggerTableRerender();
        }
    };

    const handleDeselectAll = () => {
        let hasChanges = false;

        setTableValues(prev => {
            const newTableValues = initializeTableValues(prev, 'sendReports');

            iterateCampaigns(clientCampaigns, convertTimestampsToDates, (campaign, campaignId) => {
                // Only process campaigns with open report data
                if (campaign.open_report_data) {
                    // Deselect all campaigns
                    newTableValues.sendReportsAction[campaignId] = 'no_action';
                    hasChanges = true;
                }
            });

            return hasChanges ? newTableValues : prev;
        });

        if (!hasChanges) {
            alert('No campaigns were selected to deselect.');
        } else {
            triggerTableRerender();
        }
    };

    const handleSendSelectedReports = useBatchJobSender({
        wsProvider,
        clientCampaigns,
        tableValues,
        actionKey: 'sendReportsAction',
        fromDateKey: 'sendReportsFromDate',
        toDateKey: 'sendReportsToDate',
        actionValue: 'send_report',
        createInvoice: false,
        attachInvoice: false,
        updateReportHistory: updateReportHistory,
        triggerTableRerender,
        emptyMessage: 'Please select at least one campaign to send reports for.'
    });

    return (
        <div>
            <LoadCampaignDataButton
                clientCampaigns={clientCampaigns}
                loadAllCampaignsWithHistoryAndOpenBillingData={loadAllCampaignsWithHistoryAndOpenBillingData}
                triggerTableRerender={triggerTableRerender}
                tabName="Send Reports"
            />

            <ActionButtonGroup>
                <ActionButton 
                    label="Auto-Select" 
                    onClick={handleAutoSelect}
                    variant="success"
                />
                <ActionButton 
                    label="Select All" 
                    onClick={handleSelectAll}
                    variant="info"
                />
                <ActionButton 
                    label="Deselect All" 
                    onClick={handleDeselectAll}
                    variant="secondary"
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input
                        type="checkbox"
                        id="updateReportHistory"
                        checked={updateReportHistory}
                        onChange={(e) => setUpdateReportHistory(e.target.checked)}
                        style={{ margin: 0 }}
                    />
                    <label 
                        htmlFor="updateReportHistory" 
                        style={{ 
                            fontSize: '12px', 
                            color: 'white',
                            cursor: 'pointer',
                            userSelect: 'none'
                        }}
                    >
                        Update Report History
                    </label>
                </div>
                <ActionButton 
                    label="Send Selected Reports" 
                    onClick={handleSendSelectedReports}
                    variant="primary"
                />
            </ActionButtonGroup>

            <CampaignsOverview
                clientCampaigns={clientCampaigns}
                convertTimestampsToDates={convertTimestampsToDates}
                onTableRowClick={onTableRowClick}
                tableValues={tableValues}
                setTableValues={setTableValues}
                invoicingFilter={invoicingFilter}
                setInvoicingFilter={setInvoicingFilter}
                columnsToShow="send_reports"
                tableRerenderKey={tableRerenderKey} // This will be updated by the parent component
            />
        </div>
    );
};

export default SendReportsTab; 